<template>
  <section id="career" class="career bg-decoration">
    <div class="container">
      <SectionHeader
        subtitle-key="career.subtitle"
        title-key="career.title"
        description-key="career.description"
      />
      <h4 class="openings-title">{{ $t('career.openingsTitle') }}</h4>
      <div class="career-content">
        <div class="openings-left">
          <div class="opening-list">
            <div class="opening-item glass glass-glow" v-for="(opening, index) in leftOpenings" :key="index">
              <div class="opening-info">
                <h5>{{ $t(`career.openings.${opening.key}.title`) }}</h5>
                <p class="opening-location">
                  <span v-html="locationIcon"></span>
                  {{ $t(`career.openings.${opening.key}.location`) }}
                </p>
              </div>
              <button type="button" class="btn btn-sm" @click="openExpertModal">{{ $t('career.applyNow') }}</button>
            </div>
          </div>
        </div>
        <div class="openings-right">
          <div class="opening-list">
            <div class="opening-item glass glass-glow" v-for="(opening, index) in rightOpenings" :key="index">
              <div class="opening-info">
                <h5>{{ $t(`career.openings.${opening.key}.title`) }}</h5>
                <p class="opening-location">
                  <span v-html="locationIcon"></span>
                  {{ $t(`career.openings.${opening.key}.location`) }}
                </p>
              </div>
              <button type="button" class="btn btn-sm" @click="openExpertModal">{{ $t('career.applyNow') }}</button>
            </div>
          </div>
          <div class="opening-footer glass">
            <p>{{ $t('career.noPosition') }}</p>
            <button type="button" class="btn btn-primary" @click="openExpertModal">{{ $t('career.sendResume') }}</button>
          </div>
        </div>
      </div>
    </div>
    <ExpertModal v-if="showExpertModal" @close="closeExpertModal" />
  </section>
</template>

<script setup>
import { ref } from 'vue'
import SectionHeader from './common/SectionHeader.vue'
import ExpertModal from './ExpertModal.vue'
import { getIcon } from '@/assets/icons'

const locationIcon = getIcon('location')

const allOpenings = ref([
  { key: 'project-manager', location: 'shanghai-hangzhou-wuhan' },
  { key: 'project-assistant', location: 'shanghai-hangzhou-wuhan' },
  { key: 'sales-assistant', location: 'shanghai-beijing-shenzhen' },
  { key: 'account-manager', location: 'shanghai-beijing-shenzhen' },
  { key: 'team-leader', location: 'shanghai-hangzhou-wuhan' }
])

const leftOpenings = ref(allOpenings.value.slice(0, 3))
const rightOpenings = ref(allOpenings.value.slice(3))
const showExpertModal = ref(false)

const openExpertModal = () => {
  showExpertModal.value = true
}

const closeExpertModal = () => {
  showExpertModal.value = false
}
</script>

<style scoped>
.career {
  padding: 120px 0;
  background: linear-gradient(135deg, #f1f5f9 0%, #e8f0f7 50%, #f0f4f8 100%);
  position: relative;
}

.career::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background:
    radial-gradient(circle at 10% 10%, rgba(52, 152, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 90% 90%, rgba(15, 39, 64, 0.06) 0%, transparent 50%);
  pointer-events: none;
}

.openings-title {
  font-size: 1.6rem;
  color: var(--primary-color);
  margin-bottom: 30px;
  font-weight: 700;
  text-align: left;
  position: relative;
  z-index: 1;
}

.career-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: start;
  position: relative;
  z-index: 1;
}

@media (max-width: 1024px) {
  .career-content {
    grid-template-columns: 1fr;
    gap: 40px;
  }
  .openings-title {
    text-align: center;
  }
}

.openings-left {
  position: relative;
}

.openings-right {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.opening-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.opening-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 28px 32px;
  border-radius: 24px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%);
}

.opening-item:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 40px rgba(15, 39, 64, 0.15);
}

.opening-info h5 {
  font-size: 1.2rem;
  color: var(--primary-color);
  margin-bottom: 8px;
  font-weight: 700;
}

.opening-location {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-light);
  font-size: 0.95rem;
  margin: 0;
}

.opening-location svg {
  width: 18px;
  height: 18px;
  color: var(--secondary-color);
}

.btn {
  display: inline-block;
  padding: 12px 28px;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  transition: all 0.3s ease;
  text-decoration: none;
  cursor: pointer;
  font-family: inherit;
}

.btn-sm {
  border: 2px solid var(--secondary-color);
  color: var(--secondary-color);
  background: rgba(52, 152, 255, 0.08);
  backdrop-filter: blur(10px);
}

.btn-sm:hover {
  background: var(--secondary-color);
  color: var(--white);
  transform: translateY(-3px);
  box-shadow: 0 12px 28px rgba(52, 152, 255, 0.3);
}

.btn-primary {
  background: linear-gradient(135deg, var(--secondary-color) 0%, #2980b9 100%);
  color: var(--white);
  box-shadow: 0 6px 20px rgba(52, 152, 255, 0.3);
  padding: 14px 36px;
  border: none;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #2980b9 0%, var(--secondary-color) 100%);
  transform: translateY(-3px);
  box-shadow: 0 14px 35px rgba(52, 152, 255, 0.4);
}

.opening-footer {
  padding: 32px;
  border-radius: 24px;
  text-align: center;
}

.opening-footer p {
  color: var(--text-light);
  margin-bottom: 20px;
  font-size: 1rem;
}

@media (max-width: 768px) {
  .career {
    padding: 80px 0;
  }
  .openings-title {
    font-size: 1.4rem;
    margin-bottom: 24px;
    text-align: left;
  }
  .opening-list {
    gap: 16px;
  }
  .opening-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    padding: 24px;
  }
  .opening-item .btn-sm {
    align-self: stretch;
    text-align: center;
  }
  .opening-info h5 {
    font-size: 1.15rem;
  }
  .opening-footer {
    padding: 28px 24px;
  }
  .opening-footer .btn-primary {
    padding: 14px 32px;
    font-size: 1rem;
    width: 100%;
  }
}
</style>
